import { useState, useEffect } from 'react';
import { Sparkles, Package, Image, TrendingUp, Clock, CheckCircle, XCircle, Upload, Loader2 } from 'lucide-react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useShops } from '../contexts/ShopContext';
import { useNavigate } from 'react-router-dom';
import './DashboardPage.css';

interface GenerationLog {
    id: string;
    status: 'success' | 'rejected' | 'uploaded';
    promptUsed: string;
    model: string;
    createdAt: Date;
}

export function DashboardPage() {
    const { userProfile, isAdmin } = useAuth();
    const { selectedShop: _ } = useShops();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [stats, setStats] = useState({
        totalGenerations: 0,
        weeklyGenerations: 0,
        promptsCount: 0,
        uploadedCount: 0,
    });
    const [recentLogs, setRecentLogs] = useState<GenerationLog[]>([]);
    const [weeklyData, setWeeklyData] = useState<number[]>([0, 0, 0, 0, 0, 0, 0]);

    useEffect(() => {
        if (userProfile?.uid) {
            loadDashboardData();
        }
    }, [userProfile?.uid, isAdmin]);

    const loadDashboardData = async () => {
        if (!userProfile?.uid) return;

        setIsLoading(true);

        try {
            // Get logs
            const logsRef = collection(db, 'logs');
            let logsQuery;

            if (isAdmin) {
                logsQuery = query(logsRef, orderBy('createdAt', 'desc'));
            } else {
                logsQuery = query(
                    logsRef,
                    where('userId', '==', userProfile.uid),
                    orderBy('createdAt', 'desc')
                );
            }

            const logsSnapshot = await getDocs(logsQuery);
            const logs: GenerationLog[] = [];

            logsSnapshot.forEach((doc) => {
                const data = doc.data();
                logs.push({
                    id: doc.id,
                    status: data.status,
                    promptUsed: data.promptUsed,
                    model: data.model,
                    createdAt: data.createdAt?.toDate() || new Date(),
                });
            });

            // Calculate stats
            const now = new Date();
            const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

            const weeklyLogs = logs.filter(log => log.createdAt >= oneWeekAgo);
            const uploadedLogs = logs.filter(log => log.status === 'uploaded');

            // Weekly data for chart (last 7 days)
            const dailyCounts = [0, 0, 0, 0, 0, 0, 0];
            weeklyLogs.forEach(log => {
                const dayIndex = Math.floor((now.getTime() - log.createdAt.getTime()) / (24 * 60 * 60 * 1000));
                if (dayIndex >= 0 && dayIndex < 7) {
                    dailyCounts[6 - dayIndex]++;
                }
            });

            // Get prompts count
            const promptsRef = collection(db, 'prompts');
            let promptsQuery;

            if (isAdmin) {
                promptsQuery = query(promptsRef);
            } else {
                promptsQuery = query(promptsRef, where('ownerId', '==', userProfile.uid));
            }

            const promptsSnapshot = await getDocs(promptsQuery);

            setStats({
                totalGenerations: logs.length,
                weeklyGenerations: weeklyLogs.length,
                promptsCount: promptsSnapshot.size,
                uploadedCount: uploadedLogs.length,
            });

            setRecentLogs(logs.slice(0, 5));
            setWeeklyData(dailyCounts);
        } catch (err) {
            console.error('Error loading dashboard data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'success':
                return <CheckCircle size={14} className="status-icon status-success" />;
            case 'rejected':
                return <XCircle size={14} className="status-icon status-rejected" />;
            case 'uploaded':
                return <Upload size={14} className="status-icon status-uploaded" />;
            default:
                return <Image size={14} />;
        }
    };

    const formatDate = (date: Date) => {
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return 'À l\'instant';
        if (minutes < 60) return `Il y a ${minutes}min`;
        if (hours < 24) return `Il y a ${hours}h`;
        if (days < 7) return `Il y a ${days}j`;

        return new Intl.DateTimeFormat('fr-FR', {
            day: '2-digit',
            month: 'short',
        }).format(date);
    };

    const maxWeeklyValue = Math.max(...weeklyData, 1);
    const dayLabels = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

    // Adjust labels for current day
    const today = new Date().getDay();
    const adjustedLabels = [...dayLabels.slice(today + 1), ...dayLabels.slice(0, today + 1)];

    const statCards = [
        { label: 'Images générées', value: stats.totalGenerations, icon: Image, color: 'primary' },
        { label: 'Uploadées', value: stats.uploadedCount, icon: Upload, color: 'secondary' },
        { label: 'Prompts sauvés', value: stats.promptsCount, icon: Sparkles, color: 'accent' },
        { label: 'Cette semaine', value: stats.weeklyGenerations, icon: TrendingUp, color: 'success' },
    ];

    return (
        <div className="dashboard animate-fadeIn">
            <header className="page-header">
                <div>
                    <h1 className="page-title">
                        Bonjour, <span className="text-gradient">{userProfile?.displayName}</span>
                    </h1>
                    <p className="page-subtitle">
                        Bienvenue sur le portail Duc Factory Tools
                    </p>
                </div>
            </header>

            {isLoading ? (
                <div className="loading-state">
                    <Loader2 size={32} className="spin" />
                    <p>Chargement du tableau de bord...</p>
                </div>
            ) : (
                <>
                    <section className="stats-grid">
                        {statCards.map((stat) => (
                            <div key={stat.label} className={`stat-card glass stat-${stat.color}`}>
                                <div className="stat-icon">
                                    <stat.icon size={24} />
                                </div>
                                <div className="stat-content">
                                    <span className="stat-value">{stat.value}</span>
                                    <span className="stat-label">{stat.label}</span>
                                </div>
                            </div>
                        ))}
                    </section>

                    <section className="dashboard-content">
                        {/* Weekly Activity Chart */}
                        <div className="activity-chart glass">
                            <h2 className="section-title">Activité des 7 derniers jours</h2>
                            <div className="chart-container">
                                {weeklyData.map((count, index) => (
                                    <div key={index} className="chart-bar-container">
                                        <div
                                            className="chart-bar"
                                            style={{ height: `${(count / maxWeeklyValue) * 100}%` }}
                                        >
                                            {count > 0 && <span className="bar-value">{count}</span>}
                                        </div>
                                        <span className="bar-label">{adjustedLabels[index]}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="quick-actions glass">
                            <h2 className="section-title">Actions rapides</h2>
                            <div className="actions-grid">
                                <button className="action-card" onClick={() => navigate('/generator')}>
                                    <Sparkles size={32} />
                                    <span>Générer une image</span>
                                </button>
                                <button className="action-card" onClick={() => navigate('/products')}>
                                    <Package size={32} />
                                    <span>Voir les produits</span>
                                </button>
                            </div>
                        </div>

                        {/* Recent Activity */}
                        {recentLogs.length > 0 && (
                            <div className="recent-activity glass">
                                <div className="section-header">
                                    <h2 className="section-title">Activité récente</h2>
                                    <button
                                        className="see-all-btn"
                                        onClick={() => navigate('/history')}
                                    >
                                        Voir tout
                                    </button>
                                </div>
                                <div className="activity-list">
                                    {recentLogs.map((log) => (
                                        <div key={log.id} className="activity-item">
                                            {getStatusIcon(log.status)}
                                            <div className="activity-content">
                                                <span className="activity-prompt">{log.promptUsed}</span>
                                                <span className="activity-time">
                                                    <Clock size={12} />
                                                    {formatDate(log.createdAt)}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {isAdmin && (
                            <div className="admin-notice glass">
                                <h2 className="section-title">Administration</h2>
                                <p>
                                    En tant qu'administrateur, vous pouvez configurer les boutiques Shopify
                                    et les modèles IA dans les paramètres.
                                </p>
                            </div>
                        )}
                    </section>
                </>
            )}
        </div>
    );
}
